
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { useTranslations } from "@/hooks/useTranslations";

interface CalendarHeaderProps {
  view: string;
  setView: (view: string) => void;
  settings: any;
  onCreateEvent: () => void;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onSettingsClick: () => void;
}

export const CalendarHeader = ({
  view,
  setView,
  settings,
  onCreateEvent,
  onPreviousWeek,
  onNextWeek,
  onSettingsClick
}: CalendarHeaderProps) => {
  const { language } = useLanguage();
  const { getPath } = useLanguagePath();
  const { t } = useTranslations("schedule");
  
  const viewOptions = [
    {
      value: "day",
      label: t("views.day")
    }, 
    {
      value: "week",
      label: t("views.week")
    }, 
    {
      value: "month",
      label: t("views.month")
    }
  ];

  return <div className="space-y-4 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-100/50 dark:border-gray-700/50">
        <div className="space-y-2">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href={getPath("/dashboard")}>{t("dashboard")}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{t("title")}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>
      
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center space-x-2">
          <CalendarIcon className="h-5 w-5" />
          <h2 className="text-2xl font-bold">{t("title")}</h2>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex space-x-2">
            <Button variant="outline" size="icon" onClick={onPreviousWeek}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={onNextWeek}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Select value={view} onValueChange={setView}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="dropdown-opaque">
              {viewOptions.map(option => <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={onSettingsClick}>
            {t("settings")}
          </Button>
          <Button variant="default" className="bg-flame hover:bg-flame/90 transition-colors" onClick={() => {
          if (!settings) {
            toast.error(language === 'en' ? "Configure work hours first" : "Configure primeiro os horários de trabalho");
            return;
          }
          onCreateEvent();
        }}>
            <Plus className="h-4 w-4 mr-2" />
            {t("createEvent")}
          </Button>
        </div>
      </div>
    </div>;
};
